import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowRight, Calendar, ChevronRight, Clock } from 'lucide-react';
import { getBlogArticle } from '../seo/blog-articles';

export function BlogArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getBlogArticle(slug) : undefined;

  if (!article) return <Navigate to="/blog" replace />;

  return (
    <article className="min-h-screen bg-white">
      <header className="relative overflow-hidden bg-gray-950 pt-28 pb-20 text-white md:pt-32">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900/40 via-gray-950 to-gray-950" />
        <div className="relative z-10 mx-auto max-w-3xl px-4 sm:px-6">
          <nav aria-label="Fil d'Ariane" className="mb-8 flex flex-wrap items-center gap-1 text-xs font-bold text-gray-400">
            <Link to="/" className="hover:text-white transition-colors">
              Accueil
            </Link>
            <ChevronRight className="h-3.5 w-3.5 shrink-0" />
            <Link to="/blog" className="hover:text-white transition-colors">
              Blog
            </Link>
            <ChevronRight className="h-3.5 w-3.5 shrink-0" />
            <span className="text-primary-300 line-clamp-1">{article.title}</span>
          </nav>

          <p className="mb-4 text-[10px] font-black uppercase tracking-[0.3em] text-primary-400">
            {article.badge}
          </p>
          <h1 className="text-3xl font-black tracking-tight md:text-4xl">{article.title}</h1>
          <p className="mt-6 text-lg leading-relaxed text-gray-300">{article.intro}</p>
          <div className="mt-6 flex flex-wrap items-center gap-4 text-xs font-bold text-gray-400">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" />
              {new Date(article.publishedAt).toLocaleDateString('fr-FR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              {article.readingMinutes} min de lecture
            </span>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <div className="space-y-12">
          {article.sections.map((section) => (
            <section key={section.title}>
              <h2 className="mb-4 text-2xl font-black tracking-tight text-gray-950">{section.title}</h2>
              <div className="space-y-4 text-base leading-relaxed text-gray-600">
                {section.body.map((paragraph) => (
                  <p key={paragraph.slice(0, 48)}>{paragraph}</p>
                ))}
              </div>
            </section>
          ))}
        </div>

        <section className="mt-16 border-t border-gray-100 pt-12">
          <h2 className="mb-6 text-center text-sm font-black uppercase tracking-[0.2em] text-gray-400">
            Pages associées
          </h2>
          <div className="flex flex-wrap justify-center gap-3">
            {article.relatedLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="rounded-full border border-gray-200 bg-white px-5 py-2.5 text-sm font-bold text-gray-800 shadow-sm transition hover:border-primary-300 hover:text-primary-600"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </section>

        <div className="mt-16 rounded-3xl bg-gray-950 px-8 py-10 text-center text-white">
          <h2 className="text-2xl font-black">Un photobooth pour votre mariage ?</h2>
          <p className="mx-auto mt-3 max-w-xl text-gray-400">
            L Benna Production vous répond sous 24 h — devis gratuit à Tunis et Djerba.
          </p>
          <Link
            to="/contact"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary-500 px-8 py-3 text-sm font-bold transition hover:bg-primary-400"
          >
            Demander un devis
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </article>
  );
}
